import { Box, CircularProgress, Divider, Popover, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import dayjs from "../../utils/dayjs";
import { usePerspective } from "../../contexts/app/provider";
import { GetTransformedRawParquetDocument } from "../../services/perspective_api/__generated__/perspective_server-types";
import { parseParquetData, FullParsedParquetData } from "../../utils/parquet";
import { RunSettings } from "../../services/appsync/types";
import {
  closeTransferCellPopover,
  useTransferCellPopoverTarget,
  TransferCellPopoverTarget,
} from "./store";

// One source hub feeding a "transfer order to place" cell.
interface TransferCellDetailRow {
  source_hub: string;
  sku_name: string | null;
  quantity: number;
  transit_days: number | null;
  arrival_date: number | null;
  /** Hub on-hand from the Hubs walk; null when unavailable. */
  hub_inventory: number | null;
  /** Uncapped source-hub count for the cell (rows capped server-side). */
  total_hub_count: number | null;
}

interface FetchEntry {
  loading: boolean;
  error: string | null;
  rows: TransferCellDetailRow[] | null;
}

const detailCache = new Map<string, FetchEntry>();

const toNumberOrNull = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

function arrowToRows(parsed: FullParsedParquetData): TransferCellDetailRow[] {
  return parsed.arrowTable.toArray().map((r: any) => ({
    source_hub: String(r.source_hub ?? ""),
    sku_name: r.sku_name != null ? String(r.sku_name) : null,
    quantity: toNumberOrNull(r.quantity) ?? 0,
    transit_days: toNumberOrNull(r.transit_days),
    arrival_date: toNumberOrNull(r.arrival_date),
    hub_inventory: toNumberOrNull(r.hub_inventory),
    total_hub_count: toNumberOrNull(r.total_hub_count),
  }));
}

const formatDate = (ms: number | null): string =>
  ms == null ? "—" : dayjs.utc(ms).format("MM/DD/YY");

const formatQty = (n: number | null): string =>
  n == null ? "—" : n.toLocaleString(undefined, { maximumFractionDigits: 0 });

const FIELD_LABEL_SX = {
  fontSize: "10.5px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  color: (t: any) => t.palette.atomic.muted,
};

const FIELD_VALUE_SX = {
  fontSize: "12px",
  fontVariantNumeric: "tabular-nums",
  color: (t: any) => t.palette.atomic.ink,
};

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: "16px",
        py: "3px",
      }}
    >
      <Box sx={FIELD_LABEL_SX}>{label}</Box>
      <Box sx={FIELD_VALUE_SX}>{value}</Box>
    </Box>
  );
}

/**
 * Popover for a Spokes "transfer order to place" cell: per source hub, the hub
 * on-hand inventory and the date the transfer would arrive at the spoke
 * (place period + transit; same day when transit is 0).
 *
 * Render-isolation contract: this component (and its module store) is the ONLY
 * thing that re-renders on open/close/data-arrival — never feeds state back
 * into the matrix table. Mirrors the PO cell popover.
 */
export function TransferCellPopover({
  runSettings,
}: {
  runSettings: RunSettings | null;
}) {
  const target = useTransferCellPopoverTarget();
  const { perspectiveApiService } = usePerspective();
  const theme = useTheme();
  const outlineColor = theme.palette.atomic.brandBlue;
  const [entry, setEntry] = useState<FetchEntry>({
    loading: false,
    error: null,
    rows: null,
  });

  useEffect(() => {
    if (!target) return;
    const el = target.anchorEl;
    const prevOutline = el.style.outline;
    const prevOffset = el.style.outlineOffset;
    el.style.outline = `2px solid ${outlineColor}`;
    el.style.outlineOffset = "-2px";
    return () => {
      el.style.outline = prevOutline;
      el.style.outlineOffset = prevOffset;
    };
  }, [target, outlineColor]);

  useEffect(() => {
    if (!target) return;
    const cacheKey = JSON.stringify([
      runSettings,
      target.sku,
      target.spokeLocation,
      target.sourceHub,
      target.periodStartMs,
      target.dateHierarchy,
    ]);
    const cached = detailCache.get(cacheKey);
    if (cached?.rows) {
      setEntry(cached);
      return;
    }
    const loadingEntry: FetchEntry = { loading: true, error: null, rows: null };
    detailCache.set(cacheKey, loadingEntry);
    setEntry(loadingEntry);

    const client = perspectiveApiService.getClient();
    const sub = client
      .subscribe({
        query: GetTransformedRawParquetDocument,
        variables: {
          datasetName: "supply_walk_test_spoke",
          transformId: "transfer_cell_details",
          folder: "outputs",
          runSettingsJson: runSettings ?? null,
          comparedToRunSettingsJson: null,
          format: "parquet" as const,
          params: {
            sku: target.sku,
            location: target.spokeLocation,
            ...(target.sourceHub != null
              ? { source_hub: target.sourceHub }
              : {}),
            period_start: new Date(target.periodStartMs).toISOString(),
            date_hierarchy: target.dateHierarchy,
          },
        },
      })
      .subscribe({
        next: async (res: any) => {
          const parquetData = res?.data?.getTransformedRawParquet?.parquetData;
          if (!parquetData || typeof parquetData !== "string") return;
          try {
            const parsed = await parseParquetData(
              parquetData,
              !parquetData.startsWith("http")
            );
            const rows = arrowToRows(parsed as FullParsedParquetData);
            const done: FetchEntry = { loading: false, error: null, rows };
            detailCache.set(cacheKey, done);
            setEntry(done);
          } catch (e: any) {
            const failed: FetchEntry = {
              loading: false,
              error: String(e?.message || e),
              rows: null,
            };
            detailCache.set(cacheKey, failed);
            setEntry(failed);
          }
        },
        error: (err: any) => {
          const failed: FetchEntry = {
            loading: false,
            error: String(err?.message || err),
            rows: null,
          };
          detailCache.set(cacheKey, failed);
          setEntry(failed);
        },
      });
    return () => sub.unsubscribe();
  }, [target, runSettings, perspectiveApiService]);

  if (!target) return null;

  return (
    <Popover
      open
      anchorEl={target.anchorEl}
      onClose={closeTransferCellPopover}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            padding: "12px 14px",
            minWidth: 240,
            maxWidth: 320,
            border: "1px solid",
            borderColor: (t) => t.palette.atomic.border,
            borderRadius: "8px",
          },
        },
      }}
    >
      <TransferCellPopoverContent entry={entry} />
    </Popover>
  );
}

function HeaderChip() {
  return (
    <Box
      sx={{
        display: "inline-block",
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        borderRadius: "4px",
        padding: "2px 7px",
        mb: "8px",
        backgroundColor: (t) => t.palette.atomic.blue1,
        color: (t) => t.palette.atomic.brandBlueDark,
      }}
    >
      TRANSFER TO PLACE
    </Box>
  );
}

function TransferCellPopoverContent({ entry }: { entry: FetchEntry }) {
  if (entry.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: "12px" }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (entry.error) {
    return (
      <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.red1 }}>
        Couldn't load transfer details: {entry.error}
      </Box>
    );
  }
  const rows = entry.rows ?? [];
  if (rows.length === 0) {
    return (
      <Box>
        <HeaderChip />
        <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.muted }}>
          No transfer to place for this cell.
        </Box>
      </Box>
    );
  }

  const totalCount = rows[0]?.total_hub_count ?? rows.length;

  return (
    <Box>
      <HeaderChip />
      {totalCount > rows.length && (
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: (t) => t.palette.atomic.muted,
            mb: "6px",
          }}
        >
          Showing top {rows.length} of {formatQty(totalCount)} hubs by quantity
        </Box>
      )}
      <Box sx={{ maxHeight: 320, overflowY: "auto", pr: "2px" }}>
        {rows.map((row, i) => (
          <Box key={`${row.source_hub}-${i}`}>
            {i > 0 && <Divider sx={{ my: "8px" }} />}
            <Box
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: (t) => t.palette.atomic.ink,
                mb: "6px",
              }}
            >
              {row.source_hub || "—"}
            </Box>
            <FieldRow label="Inventory at hub" value={formatQty(row.hub_inventory)} />
            <FieldRow label="Qty to place" value={formatQty(row.quantity)} />
            <FieldRow label="Arrives at spoke" value={formatDate(row.arrival_date)} />
            <FieldRow
              label="Transit"
              value={
                row.transit_days == null
                  ? "—"
                  : row.transit_days === 0
                    ? "Same day"
                    : `${row.transit_days} days`
              }
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
